/**
 * Ce qui presse chez ce client — chunk 9.BF.1.
 *
 * ## La question à laquelle ce bloc répond
 *
 * « Sur lequel de mes dossiers dois-je intervenir en premier ? » Elle ne se pose
 * qu'au niveau du client : à l'intérieur d'un dossier, la prochaine action suffit.
 *
 * ## Il ne détaille pas, il oriente
 *
 * Chaque ligne nomme le dossier et ce qui y traîne, et mène au dossier. Elle ne
 * porte **aucune action** : agir sur une échéance demande le contexte qui vit
 * dans le dossier, et le dupliquer ici donnerait deux endroits pour un même
 * geste. C'est la frontière du chunk — l'entreprise oriente, le dossier exécute.
 *
 * ## Vide, il disparaît
 *
 * Un bloc « rien ne presse » affiché en permanence apprend à ne plus le lire, et
 * le jour où quelque chose presse, il ne se distingue plus.
 */

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useCompanyDeadlines } from "../../../lib/queries/deadlines";

/** Seules ces deux qualifications appellent un regard maintenant. */
const URGENTES = new Set(["overdue", "due_soon"]);

export function CompanyPressingSection({ companyId }: { readonly companyId: string }) {
  const { t, i18n } = useTranslation();
  const echeances = useCompanyDeadlines(companyId);

  const items = (echeances.data?.items ?? []).filter((e) => URGENTES.has(e.state));
  if (echeances.isPending || items.length === 0) return null;

  return (
    <section
      aria-labelledby="ce-qui-presse"
      className="rounded-xl border border-amber-300 bg-amber-50/70 p-5"
    >
      <h2 id="ce-qui-presse" className="text-base font-semibold text-slate-900">
        {t("company_hub.pressing.title", { count: items.length })}
      </h2>

      <ul className="mt-3 divide-y divide-amber-200/70 rounded-lg border border-amber-200 bg-white">
        {items.map((e) => (
          <li key={e.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-slate-900">{e.label}</span>
              <span className="block truncate text-xs text-slate-500">
                {e.project_title}
                {e.due_at && (
                  <>
                    {" · "}
                    <span className="tabular-nums">
                      {e.state === "overdue"
                        ? t("company_hub.pressing.overdue_on", {
                            date: new Date(e.due_at).toLocaleDateString(i18n.language),
                          })
                        : t("company_hub.pressing.due_on", {
                            date: new Date(e.due_at).toLocaleDateString(i18n.language),
                          })}
                    </span>
                  </>
                )}
              </span>
            </span>
            <Link
              to={`/projects/${e.project_id}`}
              className="focus-visible:ring-brand-500 shrink-0 touch-manipulation rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              {t("company_hub.pressing.open")}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
